import Map from "ol/Map.js";
import View from "ol/View.js";
import { fromLonLat } from "ol/proj";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import { setHighlightObjectGeoJSON, setHighlightObjectStyle, setPositionsOfGeoMarkers } from "../../../stateManagement/reducers/MapSlice";
import { MapInfoWidget } from "../mapAddons/mapInfoWidget/MapInfoWidget";
import { countExtentFromGeoJSONFeatures, countExtentFromWKTFeatures, formatExtent, getImageScale, localStateReloader } from "../mapFeatures/helpers";
import { getAeroLayer, getBingLayer, getGeoJSONFeaturesVectorLayer, getRasterLayer, getWKTFeatureVectorLayer, applyAdditionalMapLayers, getGeoJSONFeaturesVectorLayers } from "../mapFeatures/layers";
import { activateCopyPointCoordinates } from "../mapFeatures/mapFunctions/copyPointCoordinates/copyPointCoordinates";
import { activateMeasureFunctions } from "../mapFeatures/mapFunctions/measureFunctions/measureInteractions";
import { activateRosreestrCadasterLayer } from "../mapFeatures/mapFunctions/rosreestrFunctions/rosreestrFunctions";
import { activateShowUserLocationFunction } from "../mapFeatures/mapFunctions/showUserLocation/showUserLocation";
import { activateMonitorScaleInfo } from "../mapFeatures/mapFunctions/mapInfoFunctions/scaleInfoFunctions";
import { applyMapLoaderForLayer, applyMapLoaderForMap } from "../mapFeatures/mapFunctions/mapLoader/MapLoader";
import { getLineStyle, getStyleForRosreestrFeature } from "../mapFeatures/styles/mapStyles";
import { useNavigate } from "react-router-dom";
import { changeCameraMarkerPosition } from "../mapFeatures/mapFunctions/getFrameOnMapPoint/getFrameOnMapPoint";
import { parseMapUrl } from "../mapFeatures/mapFunctions/parseMapUrl/parseMapUrl";
import { setNavigateToPanoButtonIsClicked, setRosreestrIsAvailable } from "../../../stateManagement/reducers/ContentHeaderSlice";
import MapRosreestrWidget from "../mapAddons/mapRosreestrWidget/MapRosreestrWidget";
import { setNumberOfSelectedRosreestrObjects, setRosreestrObjectsInfoModalIsShown, setSelectedRosreestrObjectsByGroupId } from "../../../stateManagement/reducers/RosreestrSlice";
import MapSearchbar from "../../searchbars/mapSearchbar/MapSearchbar";
import { activateAddGeoMarkersFunction } from "../mapFeatures/mapFunctions/addGeoMarkers/addGeoMarkers";
import { MapCopyCoordinatesWidget } from "../mapAddons/mapCopyCoordinatesWidget";
import { showToastMessage } from "../../../stateManagement/reducers";
import ToastMessageContainer from "../../toastMessageContainer/ToastMessageContainer";
import { activateGoToYandexMirrors } from "../mapFeatures/mapFunctions/goToYandexMirrors/goToYandexMirrors";

export default function MapWidget({ mapStateReturnFunc, target, propsWidth, propsHeight, permanentLayers, additionalLayers, propsLongitude, propsLatitude,
    propsZoom, propsMaxZoom, propsMinZoom, centerOnObjectCoordinates, propsFitExtent, propsFitExtentPadding, mapEventHandlers,
    mapInteractions, mapSizeClass, mapType, mapInfoWidgetIsShown = true, mapInfoWidgetPositionIsTop = false, showMapLoader = true, highlightObjectColor }: any) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const mapRef = useRef<Map>();
    const mapLoaderRef = useRef(null)
    const currentConfig = getItemWebStorage('currentConfig')
    const { urlZoom, urlLongitude, urlLatitude } = parseMapUrl()
    const localExtent = currentConfig.mapDefaults.extent
    const minZoom = propsMinZoom || currentConfig.mapDefaults.minZoom || 5
    const maxZoom = propsMaxZoom || currentConfig.mapDefaults.maxZoom || 20
    const localMapPosition = getItemWebStorage('mapPosition')
    const [propsCoordX, propsCoordY] = fromLonLat([propsLongitude, propsLatitude])
    const [mapStateCoordX, mapStateCoordY] = fromLonLat([localMapPosition?.longitude, localMapPosition?.latitude]);
    const [urlCoordX, urlCoordY] = fromLonLat([urlLongitude, urlLatitude]);
    const [mapDefaultsCoordX, mapDefaultsCoordY] = fromLonLat(currentConfig.mapDefaults.center);
    const coordX = propsCoordX || urlCoordX || mapStateCoordX || mapDefaultsCoordX || 0
    const coordY = propsCoordY || urlCoordY || mapStateCoordY || mapDefaultsCoordY || 0
    const zoom = propsZoom || urlZoom || localMapPosition?.zoom || currentConfig.mapDefaults.zoom || 5
    let center = [coordX, coordY]
    const extent = formatExtent(localExtent)
    const { mapPosition, highlightObjectWKTGeometry, highlightObjectGeoJSON, highlightObjectStyle, positionsOfGeoMarkers } = useAppSelector(state => state.map)
    const { areaMeasurementIsEnabled, copyCoordsButtonIsClicked, copyCoordsTypeIsChosen, 
        highContrastModeIsEnabled, navigateToPanoButtonIsClicked, perimeterMeasurementIsEnabled, 
        rosreestrIsAvailable, rosreestrIsEnabled, searchLineIsShown, showUserLocationButtonIsClicked,
        goToYandexMirrorsButtonIsClicked } = useAppSelector(state => state.contentHeader)
    const rosreestrObjectsInfoModalIsShown = useAppSelector(state => state.rosreestr.rosreestrObjectsInfoModalIsShown)
    const mapInfoIsEnabled = useAppSelector((state) => state.mapInfo.mapInfoIsShown)
    const mapTooltipIsEnabled = useAppSelector(state => state.mapTooltip.mapTooltipIsEnabled)
    const { auxiliaryLayersDisplayState, additionalLayersDisplayState } = getItemWebStorage("mapSettings") ?? {}
    const { bing: mapBingLayerIsShown, indorAvia: mapIndorAviaLayerIsShown } = auxiliaryLayersDisplayState ?? {}
    const [alertText, setAlertsShowerText] = useState<any>('')
    const [currentFitExtent, setCurrentFitExtent] = useState<any>()
    const [currentFitExtentPadding, setCurrentFitExtentPadding] = useState<any>()

    useEffect(() => {
        if (propsFitExtent) setCurrentFitExtent(propsFitExtent)
        if (propsFitExtentPadding) setCurrentFitExtentPadding(propsFitExtentPadding)
    }, [propsFitExtent, propsFitExtentPadding])

    useEffect(() => {
        if (mapPosition) {
            const { longitude, latitude, zoom } = mapPosition
            mapRef.current?.getView().setCenter(fromLonLat([longitude, latitude]))
            mapRef.current?.getView().setZoom(zoom)
            if (mapType !== 'panorama') {
                changeCameraMarkerPosition(longitude, latitude)
            }
        }
    }, [mapPosition])

    // Создание карты - базовые функции
    useEffect(() => {
        let basicLayers = [getRasterLayer(highContrastModeIsEnabled)]
        // Добавление слоев спутниковой съемки Bing
        if (mapBingLayerIsShown) {
            let bingLayer: any = getBingLayer()
            basicLayers.push(bingLayer);
        }
        // Добавление дополнительных уникальных слоев
        if (additionalLayersDisplayState) {
            basicLayers = applyAdditionalMapLayers(additionalLayersDisplayState, currentConfig, basicLayers)
        }
        // Добавление слоев съемки indorAvia
        if (mapIndorAviaLayerIsShown) {
            let aeroLayer: any = getAeroLayer()
            basicLayers.push(aeroLayer);
        }
        const map = new Map({
            moveTolerance: 3, // Клик при перемещении до 3 пикселей
            target: target ?? 'map',
            view: new View({
                projection: 'EPSG:3857',
                showFullExtent: true,
                smoothResolutionConstraint: true,
                smoothExtentConstraint: true,
                constrainResolution: true,
                constrainOnlyCenter: true,
                center: center,
                extent: extent,
                minZoom: minZoom,
                maxZoom: maxZoom,
                zoom: zoom
            }),
            pixelRatio: getImageScale().imageScale,
            layers: basicLayers
        });

        // Обозначаем объект карты
        mapRef.current = map;
        if (mapStateReturnFunc) mapStateReturnFunc(map)
        mapRef.current.on("moveend", () => localStateReloader(mapRef.current));

        if (showMapLoader) applyMapLoaderForMap(mapLoaderRef.current, mapRef.current)
        basicLayers.forEach((layer) => {
            if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
        });

        return () => {
            map.setTarget(undefined);
            dispatch(setHighlightObjectStyle(null))
            dispatch(setHighlightObjectGeoJSON(null))
            dispatch(setRosreestrObjectsInfoModalIsShown(false))
            dispatch(setPositionsOfGeoMarkers([]))
        };
    }, []);

    useEffect(() => {
        if (permanentLayers?.length > 0) {
            permanentLayers.map((layer: any) => {
                if (layer) {
                    mapRef.current?.addLayer(layer)
                    if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
                }
            })
        }
        return () => {
            permanentLayers?.map((layer: any) => {
                mapRef.current?.removeLayer(layer)
            })
        }
    }, [permanentLayers])

    // Добавление дополнительных слоев
    useEffect(() => {
        if (additionalLayers?.length > 0) {
            additionalLayers.map((layer: any) => {
                if (layer) {
                    mapRef.current?.addLayer(layer)
                    if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
                }
            })
        }
        return () => {
            additionalLayers?.map((layer: any) => {
                mapRef.current?.removeLayer(layer)
            })
        }
    }, [additionalLayers])

    useEffect(() => {
        let lineStyle = highlightObjectStyle ?? getLineStyle(highlightObjectColor, 4)
        const highlightObjectLayer = highlightObjectWKTGeometry && getWKTFeatureVectorLayer(highlightObjectWKTGeometry, lineStyle)
        if (highlightObjectLayer) {
            // mapRef.current?.addLayer(highlightObjectLayer)
            if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, highlightObjectLayer)
            const extent = countExtentFromWKTFeatures(highlightObjectWKTGeometry)
            setCurrentFitExtent(extent)
            setCurrentFitExtentPadding([50, 50, 50, 50])
        }
        return () => {
            mapRef.current?.removeLayer(highlightObjectLayer)
        }
    }, [highlightObjectWKTGeometry])

    useEffect(() => {
        if (Array.isArray(highlightObjectGeoJSON)) {
            // добавить обработку стиля не только для объектов Росреестра
            var highlightObjectLayers = highlightObjectGeoJSON && getGeoJSONFeaturesVectorLayers(highlightObjectGeoJSON, 'EPSG:3857', getStyleForRosreestrFeature)
            if (highlightObjectLayers.length > 0) {
                for (const highlightObjectLayer of highlightObjectLayers) {
                    mapRef.current?.addLayer(highlightObjectLayer)
                    if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, highlightObjectLayer)
                }
            }
        } else {
            var highlightObjectLayer = highlightObjectGeoJSON && getGeoJSONFeaturesVectorLayer(highlightObjectGeoJSON, 'EPSG:3857', highlightObjectStyle)
            if (highlightObjectLayer) {
                mapRef.current?.addLayer(highlightObjectLayer)
                if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, highlightObjectLayer)
                const extent = countExtentFromGeoJSONFeatures(highlightObjectGeoJSON, true)
                setCurrentFitExtent(extent)
                setCurrentFitExtentPadding([50, 50, 50, 50])
            }
        }

        return () => {
            if (highlightObjectLayers?.length > 0) {
                for (const highlightObjectLayer of highlightObjectLayers) {
                    mapRef.current?.removeLayer(highlightObjectLayer)
                }
            } else {
                mapRef.current?.removeLayer(highlightObjectLayer)
            }
        }
    }, [highlightObjectGeoJSON])

    // Режим высокой контрастности
    useEffect(() => {
        let newRasterLayer = getRasterLayer(highContrastModeIsEnabled)
        let layers: any = mapRef.current?.getAllLayers()
        layers.shift()
        mapRef.current?.setLayers([newRasterLayer, ...layers])
    }, [highContrastModeIsEnabled])

    // Добавление измерений на карту
    useEffect(() => {
        const measureFunction = activateMeasureFunctions(mapRef.current, perimeterMeasurementIsEnabled, areaMeasurementIsEnabled)
        return () => {
            measureFunction.off()
        }
    }, [perimeterMeasurementIsEnabled, areaMeasurementIsEnabled])

    // Обработка отсутствия доступа к Росреестру
    useEffect(() => {
        if (!rosreestrIsAvailable) {
            dispatch(showToastMessage({ messageText: 'Использование данной функции возможно только при открытии Геопортала из браузера, внесенного в реестр ПО РФ (сайт перешел на российские сертификаты безопасности)', messageType: "error", windowType: "map" }))
            dispatch(setRosreestrIsAvailable(true))
        }
    }, [rosreestrIsAvailable])

    // Подключение слоя Росреестра
    useEffect(() => {
        const rosreestrCadasterLayerFunction = activateRosreestrCadasterLayer(mapType, rosreestrIsEnabled, setAlertsShowerText, mapRef.current)
        if (!rosreestrIsEnabled) {
            dispatch(setHighlightObjectGeoJSON(null))
            dispatch(setNumberOfSelectedRosreestrObjects(0))
            dispatch(setSelectedRosreestrObjectsByGroupId(null))
        }
        return () => {
            rosreestrCadasterLayerFunction.off()
        }
    }, [rosreestrIsEnabled])

    // Функция для копирования координат точки на карте
    useEffect(() => {
        const copyCoordsFunction = activateCopyPointCoordinates(copyCoordsTypeIsChosen, copyCoordsButtonIsClicked, mapRef.current)
        return () => {
            copyCoordsFunction.off()
        }
    }, [copyCoordsTypeIsChosen, copyCoordsButtonIsClicked])

    // Функция для копирования координат точки на карте
    useEffect(() => {
        const goToYandexMirrorsFunction = activateGoToYandexMirrors(goToYandexMirrorsButtonIsClicked, mapRef.current)
        return () => {
            goToYandexMirrorsFunction.off()
        }
    }, [goToYandexMirrorsButtonIsClicked])

    useEffect(() => {
        if (navigateToPanoButtonIsClicked) {
            dispatch(setNavigateToPanoButtonIsClicked(false))
            navigate('/app/panorama')
        }
    }, [navigateToPanoButtonIsClicked])

    // Функция для отображения текущего местоположения пользователя
    useEffect(() => {
        const showUserLocationFunction = activateShowUserLocationFunction(showUserLocationButtonIsClicked, localExtent, mapRef.current)
        return () => {
            showUserLocationFunction.off()
        }
    }, [showUserLocationButtonIsClicked])

    // Функция для расставления маркеров на карте
    useEffect(() => {
        const addGeoMarkersFunction = activateAddGeoMarkersFunction(positionsOfGeoMarkers, mapRef.current)
        return () => {
            addGeoMarkersFunction.off()
        }
    }, [positionsOfGeoMarkers])

    // Отключение отображения результатов поиска при скрытии поисковой строки
    useEffect(() => {
        if (!searchLineIsShown) {
            dispatch(setHighlightObjectGeoJSON(null))
            dispatch(setPositionsOfGeoMarkers([]))
        }
    }, [searchLineIsShown])

    useEffect(() => {
        const activateMonitorScaleInfoFunction = activateMonitorScaleInfo(mapInfoIsEnabled, mapRef.current)
        return () => {
            activateMonitorScaleInfoFunction?.off()
        }
    }, [mapInfoIsEnabled])

    useEffect(() => {
        if (currentFitExtent) {
            mapRef.current?.getView().fit(currentFitExtent, {
                size: mapRef.current.getSize(),
                padding: currentFitExtentPadding,
            });
        }
    }, [currentFitExtent, currentFitExtentPadding])

    useEffect(() => {
        if (centerOnObjectCoordinates) {
            let mapSize: any = mapRef?.current?.getSize()
            mapRef?.current?.getView()?.centerOn(centerOnObjectCoordinates, mapSize, [mapSize[0] / 2, mapSize[1] / 2])
        }
    }, [centerOnObjectCoordinates])

    // Обработчик событий карты
    useEffect(() => {
        const anyOfMapToolsWithClickInteraction = (copyCoordsButtonIsClicked || perimeterMeasurementIsEnabled || areaMeasurementIsEnabled || searchLineIsShown || goToYandexMirrorsButtonIsClicked)
        let mapEventHandlersFunctions: any = []
        if (!anyOfMapToolsWithClickInteraction) {
            mapEventHandlers && mapEventHandlers?.map((mapEventHandler: any) => {
                if (mapEventHandler?.name === "mapTooltip" && mapTooltipIsEnabled) {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
                if ((mapEventHandler?.name === "objectsFind" && !mapTooltipIsEnabled) || mapEventHandler?.name === "objectsFindAlways" || mapEventHandler?.name === "transportInteraction") {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
                if (mapEventHandler?.name === "openFullSizeMap" || mapEventHandler?.name === "synchronizePrintMap") {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
                if (mapEventHandler?.name === "frameFind" || mapEventHandler?.name === "panoramaFiltersDefine" || mapEventHandler?.name === "rotatePanoramaCamera") {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
            })
        }
        return () => {
            mapEventHandlersFunctions && mapEventHandlersFunctions.map((func: any) => {
                func.off()
            })
        }
    }, [mapEventHandlers, additionalLayers, copyCoordsButtonIsClicked, perimeterMeasurementIsEnabled, 
        areaMeasurementIsEnabled, searchLineIsShown, mapTooltipIsEnabled, goToYandexMirrorsButtonIsClicked])

    useEffect(() => {
        let mapInteractionsFunctions: any = []
        mapInteractions && mapInteractions.map((mapInteraction: any) => {
            if (mapInteraction?.name === "modifyPanoramaCamera") {
                mapInteractionsFunctions.push(mapInteraction?.func(mapRef?.current))
            }
        })
        return () => {
            mapInteractionsFunctions && mapInteractionsFunctions.map((func: any) => {
                func.off()
            })
        }
    }, [mapInteractions])

    return (
        <>
            {searchLineIsShown && mapType !== "panorama" && <MapSearchbar />}
            {mapInfoWidgetIsShown && mapInfoWidgetPositionIsTop &&
                <MapInfoWidget />
            }
            {searchLineIsShown && mapType === "panorama" && <MapSearchbar />}
            <div id={target ?? 'map'} style={{ width: propsWidth, height: propsHeight }} className={(mapSizeClass ?? "full-size-map-widget") + (mapInfoWidgetIsShown ? ' with-scale-widget' : '')}>
                {/* Загрузчик карты */}
                {showMapLoader &&
                    <div className="map-loader-wrapper">
                        <div id="progress" ref={mapLoaderRef} />
                    </div>
                }
            </div>
            {/* Уведомления на карте */}
            <ToastMessageContainer windowType="map" />
            {/* Счетчик не пришедших тайлов Росреестра */}
            {alertText && rosreestrIsEnabled && <div className="rosreestr-notification">{alertText}</div>}
            {/* Блок выбора формата копируемых координат */}
            {copyCoordsButtonIsClicked && <MapCopyCoordinatesWidget isCopyButtonTypeChosen={copyCoordsTypeIsChosen} />}
            {(rosreestrObjectsInfoModalIsShown && rosreestrIsEnabled) && <MapRosreestrWidget />}
            {/* Виджет с масштабом, координатами */}
            {mapInfoWidgetIsShown && !mapInfoWidgetPositionIsTop &&
                <MapInfoWidget />
            }
        </>
    )
}
